import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedOrganisation } from '../features/organisation/organisationSlice';
import type { UserOrganisation } from '../types/types';

export const useGetUserOrganisations = (userId?: string) => {
  const [organisations, setOrganisations] = useState<UserOrganisation[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const selectedOrganisation: UserOrganisation | null = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.organisation.selected
  );
  const dispatch = useDispatch();

  const refetch = useCallback(async () => {
    if (!userId) {
      setOrganisations([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const { data, error } = await supabase
      .from('user_organisations')
      .select(
        `
        id,
        role,
        organisation:organisation_id (
          id,
          name,
          legal_name,
          organisation_type,
          status,
          country,
          registration_number,
          billing_customer_id,
          plan:plan_id (
            id,
            name,
            type,
            tier,
            features
          )
        )
      `
      )
      .eq('profile_id', userId);

    if (error) {
      console.error('Error fetching organisations:', error);
      setOrganisations([]);
    } else {
      const normalised =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (data ?? []).map((item: any) => ({
          ...item,
          organisation: {
            ...(Array.isArray(item.organisation)
              ? item.organisation[0]
              : item.organisation),
            plan: Array.isArray(item.organisation?.plan)
              ? item.organisation.plan[0]
              : item.organisation?.plan,
          },
        })) ?? [];

      setOrganisations(normalised);

      if (!selectedOrganisation && normalised.length) {
        dispatch(setSelectedOrganisation(normalised[0]));
      }
    }

    setIsLoading(false);
  }, [userId, selectedOrganisation, dispatch]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { organisations, isLoading, refetch };
};
